import {
  browserTracingIntegration,
  getCurrentScope,
  init,
  setTags,
  setUser,
  ErrorEvent,
  Exception,
  StackFrame,
} from '@sentry/browser';

import { currentUser } from 'js/utils/user';

declare global {
  interface Window {
    // Set in app/helpers/javascript_helper.rb#add_sentry_tags
    sentryTags?: Record<string, string>;

    // Set in this file
    addSentryTags?: (tags: Record<string, string>) => void;
  }
}

// Sometimes, events which were triggered from inline javascript end up with the
// full page URL in the 'filename' field of a sentry error. This can cause the
// sentry deduplication logic to not group events together correctly, because it
// interprets the different URLs as different errors. This munges the errors to
// look more similar to each other, by replacing the actual URL with the URL
// pattern. (e.g. /123123123/paper_title is replaced by /:id/:slug).
function mungeSentryEvent(event: ErrorEvent): void {
  event.fingerprint = [
    '{{ default }}',
    currentUser ? 'logged_in' : 'logged_out',
  ];

  if (window.$route_uri_pattern != null) {
    // Sentry attempts to interpret the filename as a full URL, and sometimes
    // the URI pattern doesn't look like a URL. Fix up the pattern to look
    // nicer.
    const understandableUriPattern = window.$route_uri_pattern
      // Sometimes the URI pattern contains a (.:format) at the end which isn't necessary
      .replace(/\(.:format\)$/, '')
      // Replace ( and ) in the URI pattern to make it look more like an actual URL
      .replace(/(\(|\))/g, '');

    event.exception?.values?.forEach((val: Exception) => {
      val.stacktrace?.frames?.forEach((frame: StackFrame) => {
        if (
          frame.filename &&
          frame.filename.includes(window.location.pathname)
        ) {
          frame.filename = frame.filename.replace(
            window.location.pathname,
            understandableUriPattern,
          );
        }
      });
    });
  }
}

export const initializeSentry = () => {
  const isCheckout = window.$controller_name === 'checkout';
  const dsn = isCheckout
    ? 'https://613740b08cff4fa2a45a8fb45906c50c@sentry.io/280993'
    : 'https://530c6d33a0de4ebfb124b47975ef405c@sentry.io/27953';

  // Turn on to see LOTS of information about Sentry in the console
  const debug = false;

  // Name used for grouping errors and performance spans in Sentry
  const transactionName = `${window.$controller_name}#${window.$action_name} ${
    currentUser ? '(Logged in)' : '(Logged out)'
  }`;

  init({
    dsn,
    debug, // if annoying, feel free to disable in dev by default
    release: window.$app_rev,
    environment: window.$rails_env,
    sampleRate: debug || isCheckout ? 1 : 0.01,

    // Performance monitoring and tracing
    integrations: [
      browserTracingIntegration({
        beforeStartSpan: (context) => {
          return {
            ...context,
            name: transactionName,
          };
        },
      }),
    ],
    tracesSampleRate: debug ? 1 : 0.01,

    ignoreErrors: [
      '/s/fs.js', // fullstory
      // Random plugins/extensions
      'top.GLOBALS',
      // See: http://blog.errorception.com/2012/03/tale-of-unfindable-js-error. html
      'originalCreateNotification',
      'canvas.contentDocument',
      'MyApp_RemoveAllHighlights',
      'http://tt.epicplay.com',
      "Can't find variable: ZiteReader",
      'jigsaw is not defined',
      'ComboSearch is not defined',
      'http://loading.retry.widdit.com/',
      'atomicFindClose',
      // Facebook borked
      'fb_xd_fragment',
      // ISP "optimizing" proxy - `Cache-Control: no-transform` seems to
      // reduce this. (thanks @acdha)
      // See http://stackoverflow.com/questions/4113268
      'bmi_SafeAddOnload',
      'EBCallBackMessageReceived',
      // See http://toolbar.conduit.com/Developer/HtmlAndGadget/Methods/JSInjection.aspx
      'conduitPage',
      /<unknown module> in (?:this|makeError|func|apply|api.setZoom|jQuery)/,
      '__show__deepen is not defined',
      'Blocked a frame with origin',
      // see https://github.com/mozilla/fxa-content-server/issues/4138
      'docs-homescreen-gb-container',
      'Access is denied',
      /Failed to execute 'removeChild' on 'Node': parameter \d+ is not of type 'Node'/,
      'fixedTimeID is not defined',
      "Cannot read property 'closingEls' of undefined",
      'Out of memory',
      'Cookies are not enabled',
      // Freed-script errors aren't easy to diagnose with Sentry and we send,
      // even at a 2% sample rate, millions. So I'm filtering them out to save
      // our Sentry-budget for errors we can act on.
      "Can't execute code from a freed script",
    ],
    denyUrls: [
      /seamless\.js/, // scribd
      /4gen\.js/, // scribd
      // Facebook flakiness
      /graph\.facebook\.com/i,
      // Facebook blocked
      /connect\.facebook\.net\/en_US\/all\.js/i,
      // Woopra flakiness
      /eatdifferent\.com\.woopra-ns\.com/i,
      /static\.woopra\.com\/js\/woopra\.js/i,
      // Chrome extensions
      /extensions\//i,
      /^chrome:\/\//i,
      // Other plugins
      /127\.0\.0\.1:4001\/isrunning/i, // Cacaoweb
      /webappstoolbarba\.texthelp\.com\//i,
      /metrics\.itunes\.apple\.com\.edgesuite\.net\//i,
      /kaspersky-labs\.com/i, // Kaspersky
      /adguard\.org/i, // AdGuard
    ],
    allowUrls: [
      /academia\.edu/,
      /academia-assets\.com/,
      /scholarific\.com/,
      /scholarific-assets\.com/,
      /devdemia\.com/,
      /webpack-internal:\/\//,
    ],
    beforeSend(event, hint) {
      console.error(hint.originalException || hint.syntheticException);
      mungeSentryEvent(event);
      return event;
    },
  });

  getCurrentScope().setTransactionName(transactionName);

  setTags({
    controller: window.$controller_name,
    action: window.$action_name,
    locale: window.I18n?.locale,
    language: navigator.language,
  });

  // this chunk of code is to allow assigning sentry tags while rendering the page
  window.addSentryTags = (tags) => {
    setTags(tags);
  };

  if (window.sentryTags) {
    setTags(window.sentryTags);
  }

  if (currentUser) {
    const { id, email } = currentUser;
    setUser({ id, email });
  }
};
